import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PackageCheck, Truck, Home, Package } from "lucide-react";
import { IMAGES } from "@/data/site";
import { trackOrder } from "@/lib/api";
import PageHero from "@/components/PageHero";
import Breadcrumb from "@/components/Breadcrumb";

const STEPS = [
  { key: "paid", label: "Confirmée", icon: PackageCheck },
  { key: "en_preparation", label: "En préparation", icon: Package },
  { key: "expediee", label: "Expédiée", icon: Truck },
  { key: "livree", label: "Livrée", icon: Home },
];

const stepIndex = (order) => {
  if (order.fulfillment_status === "livree") return 3;
  if (order.fulfillment_status === "expediee") return 2;
  if (order.payment_status === "paid") return 1;
  return 0;
};

const fmt = (n) => "€" + Number(n || 0).toFixed(2).replace(".", ",");

export default function OrderTracking() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", order_ref: "" });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const runTrack = useCallback(async (payload) => {
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const res = await trackOrder(payload);
      setOrder(res);
    } catch (err) {
      setError(err?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    }
    setLoading(false);
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    runTrack(form);
  };

  useEffect(() => {
    const ref = searchParams.get("ref");
    const email = searchParams.get("email");
    if (ref && email) {
      setForm({ email, order_ref: ref });
      runTrack({ email, order_ref: ref });
    }
  }, [searchParams, runTrack]);

  const active = order ? stepIndex(order) : -1;

  return (
    <>
      <PageHero
        tag="Service client"
        title="Suivi de <em>commande</em>"
        subtitle="Renseignez votre email et votre numéro de commande pour suivre l'avancement de votre colis."
        image={IMAGES.contactHero}
      />
      <Breadcrumb trail={[{ label: "Accueil", to: "/" }, { label: "Suivi de commande" }]} />

      <div className="track-layout" data-testid="order-tracking-page">
        <form onSubmit={onSubmit} className="track-form" data-testid="track-form">
          <h2 className="section-title" style={{ marginBottom: "1.25rem" }}>Retrouver ma commande</h2>
          <div className="form-field">
            <label htmlFor="track-email">Email de commande</label>
            <input id="track-email" name="email" type="email" required value={form.email} onChange={onChange} data-testid="track-email" placeholder="vous@exemple.com" />
          </div>
          <div className="form-field">
            <label htmlFor="track-ref">Numéro de commande</label>
            <input id="track-ref" name="order_ref" required value={form.order_ref} onChange={onChange} data-testid="track-ref" placeholder="ex. A1B2C3D4" />
            <span className="track-hint">Il figure dans votre email de confirmation (ex. #A1B2C3D4).</span>
          </div>
          <button type="submit" className="btn-primary" style={{ width: "100%" }} disabled={loading} data-testid="track-submit">
            {loading ? "Recherche…" : "Suivre ma commande"}
          </button>
          {error ? <div className="form-msg error" style={{ marginTop: "1rem" }} data-testid="track-error">{error}</div> : null}
        </form>

        {order ? (
          <div className="track-result" data-testid="track-result">
            <div className="track-result-head">
              <div>
                <span className="track-label">Commande</span>
                <h3 data-testid="track-order-ref">#{order.order_ref}</h3>
              </div>
              <div style={{ textAlign: "right" }}>
                <span className="track-label">Total</span>
                <h3>{fmt(order.amount)}</h3>
              </div>
            </div>

            <div className="track-timeline" data-testid="track-timeline">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = i <= active;
                return (
                  <div key={s.key} className={`track-step ${done ? "done" : ""} ${i === active ? "current" : ""}`}>
                    <div className="track-step-icon"><Icon size={18} /></div>
                    <span className="track-step-label">{s.label}</span>
                  </div>
                );
              })}
            </div>

            {order.fulfillment_status === "expediee" && order.tracking_number ? (
              <div className="track-tracking" data-testid="track-tracking-info">
                <Truck size={16} color="var(--gold)" />
                <span>{order.carrier ? `${order.carrier} — ` : ""}Suivi : <strong>{order.tracking_number}</strong></span>
              </div>
            ) : null}

            <div className="track-items">
              {order.items.map((it, i) => (
                <div key={i} className="track-item">
                  <span>{it.name} × {it.quantity}</span>
                  <span>{fmt(it.line_total)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
